import { Directive, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { MatSelect } from '@angular/material/select';

type KeyManagerLike = { setActiveItem(i: number): void; activeItemIndex?: number };

@Directive({
  selector: 'mat-select',
  standalone: true,
})
export class ProgressiveTypeaheadDirective implements OnInit, OnDestroy {
  @Input() typeaheadBufferTimeout = 1200;

  private matSelect = inject(MatSelect);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private buffer = '';
  private resetTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.elementRef.nativeElement.addEventListener('keydown', this.onKeydown, true);
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.removeEventListener('keydown', this.onKeydown, true);
    this.clearTimer();
  }

  private onKeydown = (event: KeyboardEvent): void => {
    // Escape/Tab abandon typing — wipe the buffer so the next session starts fresh.
    if (event.key === 'Escape' || event.key === 'Tab') {
      this.clearTimer();
      this.buffer = '';
      return;
    }

    if (event.key.length !== 1) return;
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    // Let mat-select handle Space when buffer is empty (opens the panel).
    if (event.key === ' ' && this.buffer === '') return;

    this.clearTimer();

    const key = event.key.toLocaleLowerCase();
    // Same-letter repeat on a single-letter buffer cycles through matches
    // instead of extending to a non-existent two-letter prefix.
    const isCycling = this.buffer.length === 1 && this.buffer === key;
    if (!isCycling) {
      this.buffer += key;
    }

    const options = this.matSelect.options?.toArray() ?? [];
    if (options.length > 0) {
      // Own the key either way so mat-select's own debounced typeahead never fires.
      event.preventDefault();
      event.stopImmediatePropagation();

      const keyManager = (this.matSelect as unknown as { _keyManager?: KeyManagerLike })._keyManager;
      const startFrom = isCycling ? (keyManager?.activeItemIndex ?? -1) + 1 : 0;

      const findMatch = (start: number): number => {
        for (let i = 0; i < options.length; i++) {
          const idx = (start + i) % options.length;
          const opt = options[idx];
          if (!opt.disabled && opt.viewValue.trim().toLocaleLowerCase().startsWith(this.buffer)) {
            return idx;
          }
        }
        return -1;
      };

      let matchIdx = findMatch(startFrom);

      // Extended prefix didn't match anything (e.g. "t" + "g" = "tg"). Don't
      // let the dead buffer linger for the rest of the timeout — drop it and
      // retry with just the new key so the user lands on the first "g" option.
      if (matchIdx < 0 && this.buffer.length > 1) {
        this.buffer = key;
        matchIdx = findMatch(0);
      }

      if (matchIdx >= 0) {
        const matched = options[matchIdx];
        keyManager?.setActiveItem(matchIdx);
        if (this.matSelect.panelOpen) {
          // Update the value through the public setter so the form control
          // syncs via ControlValueAccessor without closing the panel.
          this.matSelect.value = matched.value;
        } else {
          // Tab-and-type flow: commit normally (also closes any open panel).
          matched._selectViaInteraction();
        }
      }
    }

    this.resetTimer = setTimeout(() => {
      this.buffer = '';
      this.resetTimer = null;
    }, this.typeaheadBufferTimeout);
  };

  private clearTimer(): void {
    if (this.resetTimer) {
      clearTimeout(this.resetTimer);
      this.resetTimer = null;
    }
  }
}
